import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type {
  CalendarColor,
  Inserts,
  Tables,
  TaskPriority,
  TaskStatus,
} from '@/types/database';
import { coerceWorkloadHours } from '@/features/projects/workload';

/**
 * Hooks for the team Calendar (migration 80).
 *
 * Visibility / write model (DB-enforced via RLS): the calendar is a
 * shared org surface — every non-external_lender member can read and
 * edit everything (events, tags, attendees, attachments). See
 * 80_calendar.sql.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type CalendarTag = Tables<'calendar_tags'>;
export type CalendarEvent = Tables<'calendar_events'>;
export type CalendarEventAttachment = Tables<'calendar_event_attachments'>;

export type CalendarAttendeeProfile = Pick<
  Tables<'profiles'>,
  'id' | 'full_name' | 'email' | 'avatar_url'
>;

/** Event row with its tag + attendee profiles flattened for rendering. */
export type CalendarEventWithDetails = CalendarEvent & {
  tag: CalendarTag | null;
  attendees: CalendarAttendeeProfile[];
};

export type CalendarAttachmentWithUploader = CalendarEventAttachment & {
  uploader: CalendarAttendeeProfile | null;
};

/**
 * A project task projected onto the calendar when "Project tasks" is
 * toggled on — the current user's open assigned tasks with a due date
 * in the visible range.
 */
export interface CalendarTaskRow {
  id: string;
  name: string;
  due_date: string;
  approximate_workload_hours: number | null;
  priority: TaskPriority;
  status: TaskStatus;
  is_completed: boolean;
  project: { id: string; name: string };
}

// ---------------------------------------------------------------------------
// Query-key helpers
// ---------------------------------------------------------------------------

export const calendarKeys = {
  all: ['calendar'] as const,
  tags: () => [...calendarKeys.all, 'tags'] as const,
  events: (range: { start: string; end: string }) =>
    [...calendarKeys.all, 'events', range] as const,
  myTasks: (range: { start: string; end: string }, profileId: string) =>
    [...calendarKeys.all, 'my-tasks', range, profileId] as const,
  attachments: (eventId: string) =>
    [...calendarKeys.all, eventId, 'attachments'] as const,
  attachmentUrl: (eventId: string, path: string) =>
    [...calendarKeys.all, eventId, 'attachments', path, 'url'] as const,
};

// Storage bucket for event files (see migration 80). Path shape is
// `<organization_id>/<event_id>/<timestamp>-<filename>`.
const CALENDAR_ATTACHMENTS_BUCKET = 'calendar-attachments';

const EVENT_SELECT =
  'id, organization_id, created_by, title, event_date, start_time, end_time, location, notes, tag_id, color, created_at, updated_at, ' +
  'tag:calendar_tags(id, organization_id, created_by, name, color, created_at), ' +
  'calendar_event_attendees(profile_id, profiles!calendar_event_attendees_profile_id_fkey(id, full_name, email, avatar_url))';

type RawEventRow = CalendarEvent & {
  tag: CalendarTag | null;
  calendar_event_attendees: Array<{
    profile_id: string;
    profiles: CalendarAttendeeProfile | null;
  }>;
};

function flattenEvent(row: RawEventRow): CalendarEventWithDetails {
  const { calendar_event_attendees, ...rest } = row;
  return {
    ...rest,
    attendees: (calendar_event_attendees ?? [])
      .map((a) => a.profiles)
      .filter((p): p is CalendarAttendeeProfile => !!p),
  };
}

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

export function useCalendarTags() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: calendarKeys.tags(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calendar_tags')
        .select('id, organization_id, created_by, name, color, created_at')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CalendarTag[];
    },
  });
}

export function useCreateCalendarTag() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { name: string; color: CalendarColor }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'calendar_tags'> = {
        organization_id: profile.organization_id,
        created_by: profile.id,
        name: input.name.trim(),
        color: input.color,
      };
      const { data, error } = await supabase
        .from('calendar_tags')
        .insert(payload)
        .select('id, organization_id, created_by, name, color, created_at')
        .single();
      if (error) throw error;
      return data as CalendarTag;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: calendarKeys.tags() });
    },
  });
}

export function useDeleteCalendarTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('calendar_tags')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      // Events referencing the tag fall back to untagged (FK is
      // on-delete-set-null), so refresh both caches.
      void qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/**
 * Events whose event_date falls inside [range.start, range.end]
 * (inclusive, `YYYY-MM-DD` strings — typically the padded month grid).
 */
export function useCalendarEvents(range: { start: string; end: string }) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: calendarKeys.events(range),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calendar_events')
        .select(EVENT_SELECT)
        .gte('event_date', range.start)
        .lte('event_date', range.end)
        .order('event_date', { ascending: true })
        .order('start_time', { ascending: true, nullsFirst: true });
      if (error) throw error;
      return ((data ?? []) as unknown as RawEventRow[]).map(flattenEvent);
    },
  });
}

export interface CalendarEventInput {
  title: string;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  notes: string | null;
  tag_id: string | null;
  color: CalendarColor;
  attendeeIds: string[];
}

export function useCreateCalendarEvent() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CalendarEventInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { attendeeIds, ...fields } = input;
      const payload: Inserts<'calendar_events'> = {
        ...fields,
        organization_id: profile.organization_id,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('calendar_events')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;
      const eventId = (data as { id: string }).id;

      if (attendeeIds.length > 0) {
        const rows: Inserts<'calendar_event_attendees'>[] = attendeeIds.map(
          (profileId) => ({
            event_id: eventId,
            profile_id: profileId,
            organization_id: profile.organization_id,
          }),
        );
        const { error: attErr } = await supabase
          .from('calendar_event_attendees')
          .insert(rows);
        if (attErr) throw attErr;
      }
      return eventId;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useUpdateCalendarEvent() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { id: string } & CalendarEventInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { id, attendeeIds, ...fields } = input;
      const { error } = await supabase
        .from('calendar_events')
        .update(fields)
        .eq('id', id);
      if (error) throw error;

      // Replace the attendee set wholesale — simplest correct diff for
      // a list this small.
      const { error: delErr } = await supabase
        .from('calendar_event_attendees')
        .delete()
        .eq('event_id', id);
      if (delErr) throw delErr;
      if (attendeeIds.length > 0) {
        const rows: Inserts<'calendar_event_attendees'>[] = attendeeIds.map(
          (profileId) => ({
            event_id: id,
            profile_id: profileId,
            organization_id: profile.organization_id,
          }),
        );
        const { error: insErr } = await supabase
          .from('calendar_event_attendees')
          .insert(rows);
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useDeleteCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Grab attachment paths first so we can clean up Storage after
      // the cascade wipes the metadata rows.
      const { data: attachments } = await supabase
        .from('calendar_event_attachments')
        .select('storage_path')
        .eq('event_id', id);

      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', id);
      if (error) throw error;

      const paths = (attachments ?? []).map((a) => a.storage_path);
      if (paths.length > 0) {
        // Best-effort: a stale object is less bad than a failed delete.
        await supabase.storage.from(CALENDAR_ATTACHMENTS_BUCKET).remove(paths);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Project tasks projected onto the calendar
// ---------------------------------------------------------------------------

/**
 * Open tasks assigned to the current user whose due_date falls inside
 * the visible range. Same join strategy as useMyOpenTasks (projects
 * hooks); archived projects are dropped client-side.
 */
export function useMyCalendarTasks(
  range: { start: string; end: string },
  enabled: boolean,
) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: calendarKeys.myTasks(range, profile?.id ?? 'noop'),
    enabled: !!profile && enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select(
          'id, name, due_date, approximate_workload_hours, priority, status, is_completed, project:projects!tasks_project_id_fkey!inner(id, name, archived_at), task_assignees!inner(profile_id)',
        )
        .eq('task_assignees.profile_id', profile!.id)
        .eq('is_completed', false)
        .gte('due_date', range.start)
        .lte('due_date', range.end)
        .order('due_date', { ascending: true });
      if (error) throw error;
      type Raw = {
        id: string;
        name: string;
        due_date: string;
        approximate_workload_hours: unknown;
        priority: TaskPriority;
        status: TaskStatus;
        is_completed: boolean;
        project: { id: string; name: string; archived_at: string | null } | null;
      };
      const rows = (data ?? []) as unknown as Raw[];
      const out: CalendarTaskRow[] = [];
      for (const r of rows) {
        if (!r.project || r.project.archived_at) continue;
        out.push({
          id: r.id,
          name: r.name,
          due_date: r.due_date,
          approximate_workload_hours: coerceWorkloadHours(
            r.approximate_workload_hours,
          ),
          priority: r.priority,
          status: r.status,
          is_completed: r.is_completed,
          project: { id: r.project.id, name: r.project.name },
        });
      }
      return out;
    },
  });
}

// ---------------------------------------------------------------------------
// Event attachments — mirrors the task-attachments pattern (upload to
// Storage, then insert the metadata row; storage cleanup on delete is
// best-effort).
// ---------------------------------------------------------------------------

const ATTACHMENT_SELECT =
  'id, organization_id, event_id, uploaded_by, storage_path, file_name, mime_type, size_bytes, created_at, uploader:uploaded_by(id, full_name, email, avatar_url)';

export function useCalendarEventAttachments(eventId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: eventId
      ? calendarKeys.attachments(eventId)
      : ['calendar', 'attachments', 'noop'],
    enabled: !!profile && !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calendar_event_attachments')
        .select(ATTACHMENT_SELECT)
        .eq('event_id', eventId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CalendarAttachmentWithUploader[];
    },
  });
}

function buildCalendarAttachmentPath(args: {
  organization_id: string;
  event_id: string;
  filename: string;
}): string {
  const stamp = Date.now();
  const cleaned = args.filename.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${args.organization_id}/${args.event_id}/${stamp}-${cleaned}`;
}

export function useUploadCalendarAttachment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { eventId: string; file: File }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = buildCalendarAttachmentPath({
        organization_id: profile.organization_id,
        event_id: input.eventId,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(CALENDAR_ATTACHMENTS_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const payload: Inserts<'calendar_event_attachments'> = {
        organization_id: profile.organization_id,
        event_id: input.eventId,
        uploaded_by: profile.id,
        storage_path: path,
        file_name: input.file.name,
        mime_type: input.file.type || null,
        size_bytes: input.file.size,
      };
      const { data, error: dbErr } = await supabase
        .from('calendar_event_attachments')
        .insert(payload)
        .select(ATTACHMENT_SELECT)
        .single();
      if (dbErr) {
        // Roll back the storage object so we don't orphan bytes.
        await supabase.storage
          .from(CALENDAR_ATTACHMENTS_BUCKET)
          .remove([path]);
        throw dbErr;
      }
      return data as unknown as CalendarAttachmentWithUploader;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: calendarKeys.attachments(vars.eventId),
      });
    },
  });
}

export function useDeleteCalendarAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      eventId: string;
      storagePath: string;
    }) => {
      const { error: dbErr } = await supabase
        .from('calendar_event_attachments')
        .delete()
        .eq('id', input.id);
      if (dbErr) throw dbErr;
      await supabase.storage
        .from(CALENDAR_ATTACHMENTS_BUCKET)
        .remove([input.storagePath]);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: calendarKeys.attachments(vars.eventId),
      });
    },
  });
}

/**
 * One-off signed URL fetch for click-to-open downloads (no hook needed
 * — popup blockers tolerate the await because it runs in the click
 * handler's task).
 */
export async function getCalendarAttachmentSignedUrl(
  path: string,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(CALENDAR_ATTACHMENTS_BUCKET)
    .createSignedUrl(path, 60 * 5);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error('Could not create download link.');
  return data.signedUrl;
}

/** Short-lived signed URL so the browser can preview / download a file. */
export function useCalendarAttachmentSignedUrl(
  eventId: string | undefined,
  path: string | undefined,
) {
  return useQuery({
    queryKey:
      eventId && path
        ? calendarKeys.attachmentUrl(eventId, path)
        : ['calendar', 'attachments', 'url', 'noop'],
    enabled: !!eventId && !!path,
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      if (!path) return null;
      const { data, error } = await supabase.storage
        .from(CALENDAR_ATTACHMENTS_BUCKET)
        .createSignedUrl(path, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}
